import { safeGetJSON, safeSetJSON } from "../safeStorage";
import { isArcadeDifficulty, isArcadeGameKind, type ArcadeDifficulty, type ArcadeGameKind } from "./games";
import { isPaletteChoice, type PaletteChoice } from "./theme";

// Best scores and arcade settings are small per visitor state, so they live in
// browser storage through safeStorage and never throw.

export const BEST_SCORES_KEY = "truevined-arcade:best";
export const ARCADE_PREFS_KEY = "truevined-arcade:prefs";
export const MAX_BEST_ENTRIES = 200;

type BestTable = Record<string, number>;

const bestKey = (slug: string, game: ArcadeGameKind) => `${slug}:${game}`;

export function readBestScore(slug: string, game: ArcadeGameKind): number {
  const table = safeGetJSON<BestTable>(BEST_SCORES_KEY) || {};
  const value = table[bestKey(slug, game)];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

export function recordBestScore(slug: string, game: ArcadeGameKind, score: number): { best: number; isNew: boolean } {
  const table = safeGetJSON<BestTable>(BEST_SCORES_KEY) || {};
  const key = bestKey(slug, game);
  const previous = typeof table[key] === "number" ? table[key] : 0;
  if (score <= previous) return { best: previous, isNew: false };
  delete table[key];
  table[key] = score;
  const keys = Object.keys(table);
  for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_BEST_ENTRIES))) delete table[stale];
  safeSetJSON(BEST_SCORES_KEY, table);
  return { best: score, isNew: score > 0 };
}

export interface ArcadePrefs {
  difficulty: ArcadeDifficulty;
  palette: PaletteChoice;
  music: boolean;
  vibration: boolean;
  timingOffsetMs: number;
  touchMode: "direct" | "relative";
  sensitivity: number;
  largeTargets: boolean;
}

export const DEFAULT_ARCADE_PREFS: ArcadePrefs = { difficulty: "normal", palette: "cover", music: true, vibration: true, timingOffsetMs: 0, touchMode: "direct", sensitivity: 1, largeTargets: false };

export function readArcadePrefs(): ArcadePrefs {
  const stored = safeGetJSON<Partial<ArcadePrefs>>(ARCADE_PREFS_KEY) || {};
  return {
    difficulty: isArcadeDifficulty(stored.difficulty) ? stored.difficulty : DEFAULT_ARCADE_PREFS.difficulty,
    palette: isPaletteChoice(stored.palette) ? stored.palette : DEFAULT_ARCADE_PREFS.palette,
    music: typeof stored.music === "boolean" ? stored.music : DEFAULT_ARCADE_PREFS.music,
    timingOffsetMs: Number.isFinite(stored.timingOffsetMs) ? Math.max(-200, Math.min(200, stored.timingOffsetMs!)) : 0,
    touchMode: stored.touchMode === "relative" ? "relative" : "direct",
    sensitivity: Number.isFinite(stored.sensitivity) ? Math.max(0.5, Math.min(2, stored.sensitivity!)) : 1,
    largeTargets: stored.largeTargets === true,
    vibration: typeof stored.vibration === "boolean" ? stored.vibration : DEFAULT_ARCADE_PREFS.vibration,
  };
}

// Merges into what is stored, so the game page's settings and the stage's
// vibration switch never overwrite each other's fields.
export function writeArcadePrefs(prefs: Partial<ArcadePrefs>): ArcadePrefs {
  const merged = { ...readArcadePrefs(), ...prefs };
  safeSetJSON(ARCADE_PREFS_KEY, merged);
  return merged;
}

// The name a player posts under, remembered after the first post.
export const NICKNAME_KEY = "truevined-arcade:nickname";

export function readNickname(): string {
  const stored = safeGetJSON<{ nickname?: unknown }>(NICKNAME_KEY);
  return typeof stored?.nickname === "string" ? stored.nickname.slice(0, 40) : "";
}

export function writeNickname(nickname: string): void {
  safeSetJSON(NICKNAME_KEY, { nickname });
}

// Scores waiting to be posted: kept when a player saves a score before their
// email is confirmed, posted the next time the arcade opens with proof.
export const HELD_SCORES_KEY = "truevined-arcade:held";
export const MAX_HELD_SCORES = 5;
export const HELD_SCORE_TTL_MS = 3 * 24 * 60 * 60 * 1000;

export interface HeldScore {
  songSlug: string;
  game: ArcadeGameKind;
  score: number;
  level: number;
  durationSec: number;
  challenge?: string;
  // The server's signed receipt for the run, needed to post it later.
  runToken?: string;
  at: number;
}

const sameBoard = (a: HeldScore, b: HeldScore) => a.songSlug === b.songSlug && a.game === b.game && (a.challenge ?? "") === (b.challenge ?? "");

export function readHeldScores(now = Date.now()): HeldScore[] {
  const stored = safeGetJSON<HeldScore[]>(HELD_SCORES_KEY);
  if (!Array.isArray(stored)) return [];
  return stored
    .filter((entry) => entry && typeof entry.songSlug === "string" && isArcadeGameKind(entry.game) && Number.isFinite(entry.score) && now - Number(entry.at) < HELD_SCORE_TTL_MS)
    .slice(-MAX_HELD_SCORES);
}

// Keeps the best held score per board.
export function holdScore(entry: HeldScore): HeldScore[] {
  const held = readHeldScores(entry.at);
  const same = held.find((candidate) => sameBoard(candidate, entry));
  const next = [...held.filter((candidate) => !sameBoard(candidate, entry)), same && same.score >= entry.score ? same : entry].slice(-MAX_HELD_SCORES);
  safeSetJSON(HELD_SCORES_KEY, next);
  return next;
}

export function dropHeldScore(entry: HeldScore): void {
  safeSetJSON(HELD_SCORES_KEY, readHeldScores().filter((candidate) => !sameBoard(candidate, entry)));
}
